import type { JobSortOption } from '@/lib/job-list-display'
import {
  type ListCardsCalendarViewMode,
  syncViewModeSearchParam,
} from '@/lib/list-view-mode'

export const JOBS_LIST_RETURN_PATH_KEY = 'jobs-list-return-path'
export const JOBS_LIST_PATHNAME = '/job'
export const PROJECTS_LIST_PATHNAME = '/projects'
export const EVENTS_LIST_PATHNAME = '/events'
export const JOBS_LIST_CALENDAR_DATE_PARAM = 'date'
export const JOBS_LIST_DEFAULT_ORDERING: JobSortOption = '-created_at'
export const JOBS_LIST_VIEW_DEFAULT: ListCardsCalendarViewMode = 'list'
export const JOBS_LIST_VIEW_STORAGE_KEY = 'jobs-list-view-mode-v2'

export type JobsListPathOptions = {
  basePath?: '/job' | '/projects' | '/events'
  serviceInstanceId?: string | null
  search?: string
  orderId?: string
  statusFilter?: string
  priorityFilter?: string
  recurrenceFilter?: string
  categoryFilter?: string | number | null
  scheduleFilter?: string | null
  sortOrder?: JobSortOption | string | null
  clientUser?: string | number | null
  jobLocationId?: string | number | null
  primaryWorkerId?: string | number | null
  jobScope?: 'open' | 'completed' | null
  projectId?: string | number | null
  isProject?: 'true' | 'false' | null
  topLevel?: 'true' | 'false' | null
  dateFilter?: 'today' | 'yesterday' | 'this_month' | 'last_month' | 'custom' | null
  startDate?: string | null
  endDate?: string | null
  viewMode?: ListCardsCalendarViewMode | string | null
  calendarDate?: string | null
  page?: number
  pageSize?: number
}

export type ParsedJobsListSearch = {
  serviceInstanceId: string | null
  search: string
  orderId: string
  statusFilter: string
  priorityFilter: string
  recurrenceFilter: string
  categoryFilter: string
  scheduleFilter: string
  sortOrder: string
  clientUser: string
  jobLocationId: string
  primaryWorkerId: string
  jobScope: string
  projectId: string
  isProject: string
  topLevel: string
  dateFilter: string
  startDate: string
  endDate: string
  calendarDate: string | null
  page: number
  pageSize: number
}

function normalizeCalendarDateParam(value: string | null | undefined): string | null {
  const trimmed = String(value ?? '').trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return null
  }

  const parsed = new Date(`${trimmed}T12:00:00`)
  if (!Number.isFinite(parsed.getTime())) {
    return null
  }

  return trimmed
}

export function parseCalendarDateFromParam(value: string | null | undefined): Date | null {
  const normalized = normalizeCalendarDateParam(value)
  if (!normalized) {
    return null
  }

  return new Date(`${normalized}T12:00:00`)
}

export function parseJobsListSearch(search: string): ParsedJobsListSearch {
  const params = new URLSearchParams(search)
  const page = Number(params.get('page') || '1')
  const pageSize = Number(params.get('page_size') || '10')
  const serviceInstanceId = params.get('service_instance_id')

  return {
    serviceInstanceId: serviceInstanceId && serviceInstanceId.trim() ? serviceInstanceId : null,
    search: params.get('search') || '',
    orderId: params.get('order_id') || '',
    statusFilter: params.get('status') || 'all',
    priorityFilter: params.get('priority') || 'all',
    recurrenceFilter: params.get('recurrence_state') || 'all',
    categoryFilter: params.get('category') || 'all',
    scheduleFilter: params.get('schedule_state') || 'all',
    sortOrder: params.get('ordering') || JOBS_LIST_DEFAULT_ORDERING,
    clientUser: params.get('client_user') || '',
    jobLocationId: params.get('job_location_id') || '',
    primaryWorkerId: params.get('worker') || params.get('primary') || '',
    jobScope: params.get('job_scope') || '',
    projectId: params.get('project_id') || '',
    isProject: params.get('is_project') || '',
    topLevel: params.get('top_level') || '',
    dateFilter: params.get('date_filter') || '',
    startDate: params.get('start_date') || '',
    endDate: params.get('end_date') || '',
    calendarDate: normalizeCalendarDateParam(params.get(JOBS_LIST_CALENDAR_DATE_PARAM)),
    page: Number.isFinite(page) && page > 0 ? page : 1,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 10,
  }
}

export function isJobsListReturnPath(path: string): boolean {
  return path === '/job'
    || path.startsWith('/job?')
    || path === '/projects'
    || path.startsWith('/projects?')
    || path === '/events'
    || path.startsWith('/events?')
}

export function buildJobsListPath(options: JobsListPathOptions): string {
  const basePath = options.basePath ?? JOBS_LIST_PATHNAME
  const params = new URLSearchParams()

  if (options.serviceInstanceId) {
    params.set('service_instance_id', options.serviceInstanceId)
  }
  if (options.search?.trim()) {
    params.set('search', options.search.trim())
  }
  if (options.orderId) {
    params.set('order_id', options.orderId)
  }
  if (options.statusFilter && options.statusFilter !== 'all') {
    params.set('status', options.statusFilter)
  }
  if (options.priorityFilter && options.priorityFilter !== 'all') {
    params.set('priority', options.priorityFilter)
  }
  if (options.recurrenceFilter && options.recurrenceFilter !== 'all') {
    params.set('recurrence_state', options.recurrenceFilter)
  }
  if (options.categoryFilter && options.categoryFilter !== 'all') {
    params.set('category', String(options.categoryFilter))
  }
  if (options.scheduleFilter && options.scheduleFilter !== 'all') {
    params.set('schedule_state', options.scheduleFilter)
  }
  if (options.sortOrder && options.sortOrder !== JOBS_LIST_DEFAULT_ORDERING) {
    params.set('ordering', String(options.sortOrder))
  }
  if (options.clientUser) {
    params.set('client_user', String(options.clientUser))
  }
  if (options.jobLocationId) {
    params.set('job_location_id', String(options.jobLocationId))
  }
  if (options.primaryWorkerId) {
    params.set('worker', String(options.primaryWorkerId))
  }
  if (options.jobScope) {
    params.set('job_scope', options.jobScope)
  }
  if (options.projectId) {
    params.set('project_id', String(options.projectId))
  }
  if (options.isProject) {
    params.set('is_project', options.isProject)
  }
  if (options.topLevel) {
    params.set('top_level', options.topLevel)
  }
  if (options.dateFilter) {
    params.set('date_filter', options.dateFilter)
  }
  if (options.startDate) {
    params.set('start_date', options.startDate)
  }
  if (options.endDate) {
    params.set('end_date', options.endDate)
  }
  if (options.viewMode != null && options.viewMode !== '') {
    syncViewModeSearchParam(params, String(options.viewMode), JOBS_LIST_VIEW_DEFAULT)
  }
  if (options.calendarDate) {
    params.set(JOBS_LIST_CALENDAR_DATE_PARAM, options.calendarDate)
  }
  if ((options.page ?? 1) > 1) {
    params.set('page', String(options.page))
  }
  if ((options.pageSize ?? 10) !== 10) {
    params.set('page_size', String(options.pageSize))
  }

  const query = params.toString()
  return query ? `${basePath}?${query}` : basePath
}

export function storeJobsListReturnPath(path: string): void {
  if (isJobsListReturnPath(path)) {
    sessionStorage.setItem(JOBS_LIST_RETURN_PATH_KEY, path)
  }
}

export function getStoredJobsListReturnPath(): string | null {
  const path = sessionStorage.getItem(JOBS_LIST_RETURN_PATH_KEY)
  return path && isJobsListReturnPath(path) ? path : null
}

export function resolveJobsListReturnPath(
  returnTo: string | undefined,
  fallbackPath: string,
): string {
  if (returnTo && isJobsListReturnPath(returnTo)) {
    return returnTo
  }

  return getStoredJobsListReturnPath() ?? fallbackPath
}

export function navigateToJobsListReturnPath(
  navigate: (to: string | { pathname: string; search?: string }, options?: { replace?: boolean }) => void,
  returnPath: string,
  options?: { replace?: boolean },
): void {
  const [pathname, search = ''] = returnPath.split('?')
  navigate(
    {
      pathname,
      search: search ? `?${search}` : '',
    },
    options,
  )
}
